import { logDebug, logError, logWarning } from "../utils/logger";
import Video from "./Video";
import { QUALITIES, Quality } from "@scrolly/renderer";

const IS_HTML_MODE = typeof document === "undefined";

const MOBILE_WIDTH = 850;

const CAMERA_STARTING_POSITION = {
    fov: 10, // the lower the FOV, the smaller the distortion
    far: 50000,
    x: 0,
    y: 0,
    z: 4000,
};

export default class RendererStore {
    static MAX_ASPECT = 2;
    static MIN_ASPECT = 0.5;

    static OLD_ASPECT = 16 / 9;

    static SLIDE_HEIGHT = 4096;
    static SLIDE_WIDTH = 4096;
    static SLIDE_DEPTH = 2048;

    static SLIDE_HEIGHT_OLD = 2160;
    static SLIDE_WIDTH_OLD = 3840;
    static SLIDE_DEPTH_OLD = 2160;

    dpr: number = !IS_HTML_MODE ? window.devicePixelRatio : 1;
    fov: number | undefined = undefined;
    far: number | undefined = undefined;
    antialiasing = false;
    useOrbitControls = false;
    cameraLookAt: Vector3Tuple | undefined = undefined;
    cameraPosition: Vector3Tuple | undefined = undefined;
    width = 1;
    height = 1;
    useXScaling = false;
    useYScaling = false;
    showLights = false;
    landscape = false;
    aspectRatio = {
        name: "Auto",
        ratio: null,
    };

    viewportTop = 0;
    viewportLeft = 0;
    viewportWidth = 1;
    viewportHeight = 1;
    // boundsLerpInterpolated: Function | null = null

    /**
     * In the editor we expect things to work differently because e.g.
     * layers change and come and go
     */
    _usedInEditor = false;
    layers: Array<string> = [];
    loadedLayers: Array<string> = [];
    finishedLoading = false;

    useVideoPlayWorkaround = false;
    videosThatShouldBePlaying = new Set<Video>();

    useOutlines = false;

    resizeFunction: (() => void) | undefined = undefined;

    numberOfFrames = 0;
    activatedStats = false;
    startDate: Date = new Date();
    endDate: Date = new Date();

    onProgress: (progress: number) => void;

    constructor(onProgress: (progress: number) => void) {
        this.cameraLookAt = [0, 0, 0];
        this.resetCamera();

        this.setAntialiasing = this.setAntialiasing.bind(this);
        this.setHeight = this.setHeight.bind(this);
        this.setWidth = this.setWidth.bind(this);
        this.setUseXScaling = this.setUseXScaling.bind(this);
        this.setUseYScaling = this.setUseYScaling.bind(this);
        this.toggleOrbitControls = this.toggleOrbitControls.bind(this);
        this.toggleShowLights = this.toggleShowLights.bind(this);
        this.toggleOutlines = this.toggleOutlines.bind(this);
        this.resetCamera = this.resetCamera.bind(this);
        this.onProgress = onProgress;
    }

    setResizeFunction(resizeFunction: TODOAny) {
        this.resizeFunction = resizeFunction;
    }

    setAspecRatio(aspectRatio: TODOAny) {
        this.aspectRatio = aspectRatio;
    }

    toggleLandscape() {
        this.landscape = !this.landscape;
    }

    setUseXScaling(useIt: boolean) {
        this.useXScaling = useIt;
    }

    setAntialiasing(antialiasing: boolean) {
        this.antialiasing = antialiasing;
    }

    setUseYScaling(useIt: boolean) {
        this.useYScaling = useIt;
    }

    setWidth(width: TODOAny, setViewport = true) {
        this.width = width;
        if (setViewport) {
            this.viewportWidth = width;
        }
    }

    setHeight(height: TODOAny, setViewport = true) {
        this.height = height;
        if (setViewport) {
            this.viewportHeight = height;
        }
    }

    setBounds(height: number, width: number, top: number, left: number) {
        this.viewportHeight = height;
        this.viewportWidth = width;
        this.viewportTop = top;
        this.viewportLeft = left;
    }

    get useMobile() {
        return this.viewportWidth < MOBILE_WIDTH;
    }

    get quality() {
        const currentSize = Math.max(this.width, this.height) * this.dpr;
        const qualityMin = Math.min(1, currentSize / RendererStore.SLIDE_WIDTH);
        // check if it's a phone, then the maxqality should be 1536
        const isPhone = this.width < 500 || this.height < 500;
        const qualityMaxKey = isPhone ? Quality.Q768 : Quality.Q4096;
        const qualityMax = QUALITIES.find((q) => q.quality === qualityMaxKey);
        const index = QUALITIES.findIndex((p) => p.fraction >= qualityMin);
        // 1 will only slighly reduce the quality, but improve performance and load times.
        // only reduce if the choosen currently for high dpi screens
        let choosenQuality = QUALITIES[index];
        if (choosenQuality.quality === Quality.Q3072 || choosenQuality.quality === Quality.Q4096) {
            choosenQuality = QUALITIES[index - 1];
        }
        return choosenQuality.fraction > qualityMax!.fraction ? qualityMax : choosenQuality;
    }

    resetCamera() {
        this.fov = CAMERA_STARTING_POSITION.fov;
        this.far = CAMERA_STARTING_POSITION.far;
        this.cameraPosition = [CAMERA_STARTING_POSITION.x, CAMERA_STARTING_POSITION.y, CAMERA_STARTING_POSITION.z];
        this.useOrbitControls = false;
    }

    startFunction = () => {
        logWarning("Called startFunction before setStartFunction");
    };

    setStartFunction = (startFunction: TODOAny) => {
        this.startFunction = () => {
            startFunction();
            this.startFunction = () => {};
        };
    };

    isUsedInEditor() {
        return this._usedInEditor;
    }

    setUsedInEditor(flag: boolean) {
        this._usedInEditor = flag;
    }

    setLayers(layers: Array<string>) {
        this.layers = Array.from(new Set(layers));
    }

    loadedLayer(layerId: string, url: string) {
        if (!this.isUsedInEditor()) {
            if (!this.loadedLayers.includes(layerId) && !this.layers.includes(layerId)) {
                logError(new Error(`Tried to set unknown layer as loaded: ${layerId}`));
                return;
            }
            if (this.loadedLayers.includes(layerId)) {
                logError(new Error(`Tried to insert duplicate into loadedLayers: ${layerId}`));
                return;
            }
        }

        logDebug(`LOADED layer ${layerId} ${url}`);
        this.loadedLayers.push(layerId);
        if (this.loadedLayers.length >= this.layers.length && !this.finishedLoading) {
            logDebug("finished loading");
            this.finishedLoading = true;
        }
        this.onProgress(this.progress);
    }

    resetLayers() {
        this.onProgress(0);
        this.layers = [];
        this.loadedLayers = [];
        this.finishedLoading = false;
    }

    toggleOrbitControls() {
        this.useOrbitControls = !this.useOrbitControls;
    }

    toggleShowLights() {
        this.showLights = !this.showLights;
    }

    toggleOutlines() {
        this.useOutlines = !this.useOutlines;
    }

    get progress() {
        if (this.isUsedInEditor()) {
            logError(new Error("progress is not implemented in editor mode"));
            return 1.0;
        }

        const value = this.layers.length > 0 ? this.loadedLayers.length / this.layers.length : 0;
        if (value > 1.0) {
            logError(new Error(`Reached loaded progress greater than 1.0: ${value}`));
        }
        return value;
    }

    static cameraAspect(width: TODOAny, height: TODOAny) {
        return width / height;
    }

    static visibleSize(fov: TODOAny, cameraPositionZ: TODOAny, cameraAspect: TODOAny) {
        // update height/width stuff
        // Here we calculate the visible height/width at z = 0,
        // The math behind that can be found in this stackoverflow post:
        // https://stackoverflow.com/questions/13350875/three-js-width-of-view/13351534#13351534
        const DEG2RAD = Math.PI / 180;
        const vFOV = DEG2RAD * fov; // convert vertical fov to radians

        const visibleHeight = 2 * Math.tan(vFOV / 2) * cameraPositionZ; // visible height
        const visibleWidth = visibleHeight * cameraAspect; // visible width
        return {
            height: visibleHeight,
            width: visibleWidth,
        };
    }

    calculateBounds() {
        // const zoom = this.zoom;
        const { viewportWidth } = this;
        const { viewportHeight } = this;
        // const zoomYOffset = viewportHeight - viewportHeight * zoom;
        // const zoomXOffset = this.viewportLeft * zoom + (viewportWidth - viewportWidth * zoom);

        const deviceAspect = viewportWidth / viewportHeight;

        // bound our aspect ratio from 0.5 to 2
        const closestPossibleAspect = Math.max(
            Math.min(deviceAspect, RendererStore.MAX_ASPECT),
            RendererStore.MIN_ASPECT
        );
        const useMaxWidth = closestPossibleAspect > 1.0;

        // the width / height we will actually use
        const width = useMaxWidth ? viewportWidth : viewportHeight * closestPossibleAspect;
        const height = useMaxWidth ? viewportWidth / closestPossibleAspect : viewportHeight;
        /// how much of the filler will we cut?
        const cutOffFillerX = useMaxWidth ? 0 : (1 - closestPossibleAspect) / RendererStore.MIN_ASPECT;
        // TODO: investigate more
        const cutOffFillerY = useMaxWidth ? (1 - viewportHeight / viewportWidth) / RendererStore.MIN_ASPECT : 0;

        // TODO: remove magic numbers
        const safeAreaSizeXRelative = 1 / (2 - cutOffFillerX);
        const safeAreaSizeYRelative = 1 / (2 - cutOffFillerY);

        const bounds = {
            newTop: 0,
            newLeft: 0,
            newHeight: height,
            newWidth: width,
        };

        return {
            bounds,
            cutOffFillerX,
            cutOffFillerY,
            closestPossibleAspect,
            deviceAspect,
            useMaxWidth,
            safeAreaSizeXRelative,
            safeAreaSizeYRelative,
        };
    }

    collectStats() {
        if (this.activatedStats) {
            this.numberOfFrames += 1;
        }
    }

    resetStats() {
        this.numberOfFrames = 0;
        const newDate = new Date();
        this.startDate = newDate;
        this.endDate = newDate;
    }

    activateStats() {
        this.activatedStats = true;
    }

    deactivateStats() {
        this.activatedStats = false;
        this.endDate = new Date();
    }
}
